import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, InputAdornment, Paper, Step, StepLabel, Stepper, Tab, Tabs, TextField, Typography, useMediaQuery,
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AmendmentsReview } from 'views/imports/components/amendments-review';
import { AmountConfirmDialog } from 'views/imports/components/amount-confirm-dialog';
import { ExtractControls } from 'views/imports/components/extract-controls';
import { FileDropzone } from 'views/imports/components/file-dropzone';
import { IssuesList } from 'views/imports/components/issues-list';
import { LineList, RemovedRows } from 'views/imports/components/line-list';
import { MarkdownView } from 'views/imports/components/markdown-view';
import { PageList } from 'views/imports/components/page-list';
import { SourceViewer } from 'views/imports/components/source-viewer';
import { TargetAndHandoff } from 'views/imports/components/target-and-handoff';
import { useImportsState } from 'views/imports/use-imports-state';
import { relatedFilterFor } from 'views/imports/row-filter';

const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {
    padding: theme.spacing(2),
    [theme.breakpoints.up('sm')]: { padding: theme.spacing(3) },
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  dataPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  sendRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  viewer: {
    position: 'sticky',
    top: theme.spacing(2),
    alignSelf: 'flex-start',
  },
  fillColumn: {
    display: 'flex',
    flexDirection: 'column',
  },
  fillContainer: {
    flex: 1,
  },
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  tab: {
    textTransform: 'none',
  },
  sendButton: {
    textTransform: 'none',
  },
  steps: {
    position: 'sticky',
    top: theme.spacing(7),
    zIndex: 3,
    padding: theme.spacing(1, 0),
    backgroundColor: theme.palette.background.default,
    ...(theme.palette.mode === 'dark' && {
      '& .MuiStepIcon-root .MuiStepIcon-text': { fill: theme.palette.common.black },
      '& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed': {
        color: theme.palette.tab.offselected,
      },
    }),
  },
  stepNav: {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 3,
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    paddingBottom: `calc(${theme.spacing(1)} + env(safe-area-inset-bottom))`,
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
  },
  stepNavSpacer: {
    height: `calc(${theme.spacing(8)} + env(safe-area-inset-bottom))`,
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dialogContent: {
    padding: theme.spacing(0, 2, 2),
  },
}));

type CentreTab = 'list' | 'markdown' | 'restore';

const MOBILE_FOCUS_ZOOM = 400;

const resolveTab = (requested: CentreTab, hasRemoved: boolean, compact: boolean): CentreTab => {
  if (requested === 'restore' && !hasRemoved) return 'list';
  if (requested === 'markdown' && compact) return 'list';
  return requested;
};

export const ImportsView = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const state = useImportsState();
  const compact = useMediaQuery(theme.breakpoints.down('lg'));
  const [centreTab, setCentreTab] = useState<CentreTab>('list');
  const [targetOpen, setTargetOpen] = useState(false);
  const [sourceOpen, setSourceOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [step, setStep] = useState(0);
  const previousPhase = useRef(state.phase);
  const busy = state.phase !== 'idle';

  useEffect(() => {
    const extractionRan = ['ocr', 'extracting', 'reviewing'].includes(previousPhase.current);
    previousPhase.current = state.phase;
    if (extractionRan && state.phase === 'idle' && compact && state.hasResults && !state.cancelled) setStep(1);
  }, [state.phase, state.hasResults, state.cancelled, compact]);
  const hasRemoved = state.removedRows.length > 0;
  const activeTab = resolveTab(centreTab, hasRemoved, compact);

  if (centreTab === 'restore' && !hasRemoved) setCentreTab('list');
  const fill = !state.hasResults;
  const steps = [t('imports:steps.document'), t('imports:steps.review'), t('imports:steps.send')];

  const documentPanel = (
    <Paper className={classes.panel}>
      <FileDropzone disabled={busy} onFiles={state.addFiles} onRejected={state.rejectFiles} onNoFile={state.noFileReceived} />
      <ExtractControls
        phase={state.phase}
        progress={state.progress}
        canExtract={state.canExtract}
        serviceReady={state.serviceReady}
        hasPages={state.hasPages}
        onExtract={state.extract}
        cancelling={state.cancelling}
        onCancel={state.cancel}
        onClear={state.clear}
        selectionHint={state.needsSelection}
        reviewEnabled={state.reviewEnabled}
        onToggleReview={state.setReviewEnabled}
        reviewFailedCount={state.reviewFailedCount}
        onRetryReview={state.retryReview}
        scanTables={state.scanTables}
        onToggleScanTables={state.setScanTables}
        extractIsSecondary={state.hasResults}
      />
      <IssuesList issues={state.issues} pages={state.pages} cancelled={state.cancelled} />
      <AmendmentsReview
        rows={state.unmatchedAmendments}
        pages={state.pages}
        disabled={busy}
        onAccept={state.acceptAmendment}
        onDismiss={state.dismissAmendment}
      />
      <AmountConfirmDialog
        amendment={state.pendingAmount}
        current={state.pendingAmountCurrent}
        onConfirm={state.confirmPendingAmount}
        onCancel={state.cancelPendingAmount}
      />
      <PageList
        pages={state.pages}
        ocrByPage={state.ocrByPage}
        extractedPageIds={state.extractedPageIds}
        selectedPageId={state.previewPageId}
        onSelectPage={state.selectPreviewPage}
        selectedPageIds={state.selectedPageIds}
        maxSelected={state.maxSelectedPages}
        onToggleSelected={state.togglePageSelected}
        onClearSelection={state.clearSelection}
        selectionDisabled={busy}
      />
    </Paper>
  );

  const sendButton = (
    <div className={classes.sendRow}>
      <Button
        variant="contained"
        color="secondary"
        disableElevation
        className={classes.sendButton}
        onClick={() => setTargetOpen(true)}
      >
        {t('imports:handoff.button')}
      </Button>
    </div>
  );

  const reviewContent = (
    <>
      <Tabs
        value={activeTab}
        onChange={(_event, next: CentreTab) => setCentreTab(next)}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabs}
        aria-label={t('imports:tabs.label')}
      >
        <Tab value="list" label={t('imports:tabs.list')} className={classes.tab} />
        {!compact && <Tab value="markdown" label={t('imports:tabs.markdown')} className={classes.tab} />}
        {hasRemoved && <Tab value="restore" label={t('imports:tabs.restore')} className={classes.tab} />}
      </Tabs>
      {activeTab !== 'restore' && (
        <TextField
          size="small"
          variant="outlined"
          fullWidth
          value={filterText}
          placeholder={t('imports:filter.placeholder')}
          onChange={(event) => setFilterText(event.target.value)}
          inputProps={{ 'aria-label': t('imports:filter.label') }}
          InputProps={{
            endAdornment: filterText === '' ? undefined : (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={t('imports:filter.clear')}
                  onClick={() => setFilterText('')}
                >
                  <FontAwesomeIcon icon={faTimes} size="sm" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}
      {activeTab === 'restore' && (
        <RemovedRows
          removed={state.removedRows}
          disabled={busy}
          onRestore={state.restoreRow}
        />
      )}
      {activeTab === 'list' && (
        <LineList
          lines={state.parsed.lines}
          filter={filterText}
          originals={state.originals}
          selectedId={state.selectedId}
          selectionOrigin={state.selectionOrigin}
          inlineAmendments={state.inlineAmendments}
          disabled={busy}
          onSelect={state.selectLine}
          onShowOnly={setFilterText}
          onOpenSource={compact ? () => setSourceOpen(true) : undefined}
          onChange={state.setRowValue}
          onReset={state.resetRow}
          onRemove={state.removeRow}
          onAccept={state.acceptAmendment}
          onDismiss={state.dismissAmendment}
        />
      )}
      {activeTab === 'markdown' && <MarkdownView markdown={state.markdown} filter={filterText} />}
    </>
  );

  const targetDialog = (
    <TargetAndHandoff
      markdown={state.markdown}
      parsed={state.parsed}
      open={targetOpen}
      onClose={() => setTargetOpen(false)}
      onTargetChange={state.changeTarget}
      onSetDataValue={state.setDataValue}
      onSent={state.markSent}
    />
  );

  const sourceViewer = (zoomFill: boolean, focusZoom?: number, showEnlarge?: boolean, showHeading?: boolean) => (
    <SourceViewer
      pages={state.pages}
      linesByPage={state.linesByPage}
      selectedSource={state.selectedSource}
      onSelectSource={(src) => {
        const key = state.selectSource(src);
        if (key !== null) setFilterText(relatedFilterFor(key));
      }}
      linkedKeys={state.linkedSourceKeys}
      unusedSource={state.unusedSource}
      noSourceSelected={state.selectedLineHasNoSource}
      previewPageId={state.previewPageId}
      onPageChange={state.selectPreviewPage}
      fill={zoomFill}
      focusZoom={focusZoom}
      showEnlarge={showEnlarge}
      showHeading={showHeading}
    />
  );

  const canGoNext = step === 0 ? state.hasResults : step < steps.length - 1;

  return (
    <div className={classes.root}>
      <Typography variant="h5" component="h1" className={classes.title}>
        {t('imports:title')}
      </Typography>
      {compact ? (
        <>
          <Stepper activeStep={step} className={classes.steps} aria-label={t('imports:steps.label')}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {step === 0 && documentPanel}
          {step === 1 && <Paper className={classes.dataPanel}>{reviewContent}</Paper>}
          {step === 2 && (
            <Paper className={classes.dataPanel}>
              {sendButton}
              {targetDialog}
            </Paper>
          )}
          <div className={classes.stepNav}>
            <Button color="inherit" disabled={step === 0} onClick={() => setStep((current) => current - 1)}>
              {t('imports:steps.back')}
            </Button>
            {step < steps.length - 1 && (
              <Button color="inherit" disabled={!canGoNext} onClick={() => setStep((current) => current + 1)}>
                {t('imports:steps.next')}
              </Button>
            )}
          </div>
          <div className={classes.stepNavSpacer} aria-hidden="true" />
          <Dialog fullScreen open={sourceOpen} onClose={() => setSourceOpen(false)} aria-labelledby="imports-source-title">
            <DialogTitle id="imports-source-title" className={classes.dialogTitle}>
              <Typography variant="h6">{t('imports:viewer.title')}</Typography>
              <IconButton
                aria-label={t('imports:steps.close')}
                onClick={() => setSourceOpen(false)}
                size="large"
              >
                <FontAwesomeIcon icon={faTimes} />
              </IconButton>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
              {sourceViewer(false, MOBILE_FOCUS_ZOOM, false, false)}
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            {documentPanel}
          </Grid>
          {state.hasResults && (
            <Grid item xs={12} md={5}>
              <Paper className={classes.dataPanel}>
                {sendButton}
                {reviewContent}
                {targetDialog}
              </Paper>
            </Grid>
          )}
          <Grid item xs={12} md={fill ? 9 : 4} className={fill ? classes.fillColumn : undefined}>
            <Grid container spacing={2} className={fill ? classes.fillContainer : undefined}>
              <Grid item xs={12} className={state.hasResults ? classes.viewer : undefined}>
                {sourceViewer(true)}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default ImportsView;
